// Kiểu dữ liệu + dữ liệu mẫu cho module Thẻ liệu trình (giai đoạn UI-first, chưa có backend)
import { CATALOG, type PaymentMethod } from '../POS/data';

/** session: gói theo buổi · value: thẻ trả trước, trừ tiền theo giá dịch vụ */
export type CardKind = 'session' | 'value';
/** main: dịch vụ khách trả tiền · bonus: dịch vụ tặng kèm (không tính giá trị khi hoàn tiền) */
export type ItemRole = 'main' | 'bonus';
/** pos: thẻ bán qua POS (tiền nằm trên đơn POS) · import: thẻ chuyển từ hệ thống cũ */
export type CardSource = 'pos' | 'import';
export type CardBaseStatus = 'active' | 'refunded' | 'cancelled';
export type CardStatus = 'active' | 'expired' | 'completed' | 'refunded' | 'cancelled';
/** Hạn dùng tính từ: ngày mua · buổi dùng đầu tiên (thẻ chờ kích hoạt) · không thời hạn */
export type ValidityBasis = 'purchase' | 'first_use' | 'unlimited';

export interface TService { id: string; code: string; name: string; group: string; price: number }

export interface PackageItem { serviceId: string; sessions: number; role: ItemRole }

export interface TreatmentPackage {
  id: string;
  code: string;
  name: string;
  category: string;
  kind: CardKind;
  /** Thẻ buổi: dịch vụ + số buổi. Thẻ giá trị: dịch vụ được dùng (rỗng = mọi dịch vụ) */
  items: PackageItem[];
  /** Thẻ giá trị: mệnh giá được dùng (thường lớn hơn giá bán) */
  faceValue: number;
  price: number;
  validityMonths: number;
  /** Cách tính hạn dùng (không có = từ ngày mua) */
  validityBasis?: ValidityBasis;
  /** Hạn dùng tính từ buổi đầu: phải dùng buổi đầu trong N tháng kể từ ngày mua */
  activationMonths?: number;
  /** Số đợt thanh toán tối đa (1 = trả một lần) */
  maxInstallments: number;
  /** % tối thiểu phải thu ở đợt 1 */
  minFirstPct: number;
  /** Quy định tiền riêng của gói (thiếu trường nào thì lấy mặc định – xem pricingOf) */
  pricing?: Partial<PackagePricing>;
  /** Lịch sử đổi giá / chính sách giá */
  priceHistory?: PriceChange[];
  active: boolean;
  note: string;
  updatedAt: string;
}

/** Quy định tiền cho từng gói */
export interface PackagePricing {
  /** Giá riêng theo chi nhánh (không có = dùng giá niêm yết) */
  branchPrices: Record<string, number>;
  /** Giá khuyến mãi có thời hạn (YYYY-MM-DD, tính cả 2 đầu) */
  promo: { price: number; from: string; to: string; label: string } | null;
  /** Chiết khấu tay tối đa khi bán (%), áp cùng hạn mức theo vai trò – lấy mức thấp hơn */
  maxDiscountPct: number;
  /** Phụ thu khi trả từ 2 đợt trở lên (% trên giá bán) */
  installmentSurchargePct: number;
  /** Khoảng cách mặc định giữa các đợt (ngày) */
  intervalDays: number;
  /** Cho phép hoàn tiền */
  allowRefund: boolean;
  /** Phí hoàn tiền (% trên số được hoàn) */
  refundFeePct: number;
  /** Phí gia hạn mỗi tháng */
  extensionFeePerMonth: number;
  /** Tổng số tháng được gia hạn tối đa (0 = không cho gia hạn) */
  maxExtensionMonths: number;
}

export interface PriceChange { at: string; by: string; changes: string[] }

/** Quy định tiền chụp vào thẻ lúc bán — đổi chính sách gói sau này không ảnh hưởng thẻ đã bán */
export type CardPolicy = Pick<PackagePricing, 'allowRefund' | 'refundFeePct' | 'extensionFeePerMonth' | 'maxExtensionMonths'>;

export interface CardLine {
  serviceId: string;
  name: string;
  group: string;
  role: ItemRole;
  /** Số buổi theo gói */
  total: number;
  /** Buổi điều chỉnh / tặng thêm sau khi bán (có thể âm) */
  bonus: number;
  /** Buổi đã dùng trước khi chuyển sang hệ thống (thẻ import) */
  usedBefore: number;
  /** Giá trị phân bổ cho 1 buổi (0 với dịch vụ tặng) */
  unitValue: number;
}

export interface Installment { no: number; dueDate: string; amount: number }

export interface CardPayment {
  id: string;
  at: string;
  amount: number;
  method: PaymentMethod;
  txnRef: string;
  by: string;
  branchId: string;
  /** Mã giao dịch POS tương ứng (thẻ bán qua POS) */
  posTxnId?: string;
}

export interface UsageItem { serviceId: string; name: string; qty: number; value: number }

export interface CardUsage {
  id: string;
  at: string;
  branchId: string;
  staffId: string;
  items: UsageItem[];
  by: string;
  note: string;
  signed: boolean;
  bookingId?: string;
  overrideBy?: string;
  overrideReason?: string;
  cancelled?: { at: string; by: string; reason: string };
}

export type BookingStatus = 'booked' | 'done' | 'cancelled' | 'no_show';

export interface CardBooking {
  id: string;
  at: string;
  branchId: string;
  serviceId: string;
  name: string;
  staffId: string;
  note: string;
  status: BookingStatus;
  createdAt: string;
  by: string;
  usageId?: string;
  cancelReason?: string;
}

export type CardRefundStatus = 'pending' | 'paid' | 'rejected';

export interface CardRefund {
  id: string;
  at: string;
  by: string;
  mode: 'partial' | 'full';
  /** Buổi trả lại (thẻ buổi) */
  sessions: { serviceId: string; qty: number }[];
  /** Mệnh giá trả lại (thẻ giá trị) */
  valueReturned: number;
  amount: number;
  fee: number;
  method: PaymentMethod;
  txnRef: string;
  reason: string;
  status: CardRefundStatus;
  approvedBy?: string;
  approvedAt?: string;
  rejectReason?: string;
  /** Phiếu hoàn POS tương ứng — duyệt / chi tiền ở màn Hoàn tiền POS */
  posRefundId?: string;
}

export type CardEventKind = 'issue' | 'payment' | 'use' | 'cancel_use' | 'extend' | 'adjust' | 'booking' | 'refund' | 'plan' | 'print';

export interface CardEvent { at: string; by: string; kind: CardEventKind; text: string; amount?: number }

export interface TreatmentCard {
  id: string;
  customerId: string;
  customerName: string;
  customerPhone: string;
  packageId: string;
  packageCode: string;
  packageName: string;
  category: string;
  kind: CardKind;
  branchId: string;
  soldBy: string;
  consultant: string;
  createdAt: string;
  /** Ngày hết hạn (YYYY-MM-DD, dùng hết ngày này). Thẻ chờ kích hoạt: hạn phải dùng buổi đầu. Không thời hạn: NO_EXPIRY */
  expiresAt: string;
  /** Cách tính hạn và số tháng hạn dùng chụp từ gói lúc bán (thẻ cũ không có = từ ngày mua) */
  validityBasis?: ValidityBasis;
  validityMonths?: number;
  /** Thời điểm kích hoạt (buổi dùng đầu) với thẻ tính hạn từ buổi đầu */
  activatedAt?: string;
  listPrice: number;
  /** Giá bán sau chiết khấu — tổng phải thu */
  price: number;
  lines: CardLine[];
  /** Thẻ giá trị */
  faceValue: number;
  valueAdjust: number;
  valueUsedBefore: number;
  allowedServiceIds: string[];
  installments: Installment[];
  payments: CardPayment[];
  usages: CardUsage[];
  bookings: CardBooking[];
  refunds: CardRefund[];
  events: CardEvent[];
  status: CardBaseStatus;
  source: CardSource;
  orderId?: string;
  lineKey?: string;
  note: string;
  /** Quy định tiền của gói tại thời điểm bán */
  policy?: CardPolicy;
  /** Tổng số tháng đã gia hạn */
  extendedMonths?: number;
}

export type PayRequestStatus = 'pending' | 'paid' | 'rejected' | 'cancelled';

/**
 * Yêu cầu thu tiền thẻ CMS gửi sang máy POS. Tiền chỉ được ghi nhận khi POS xác nhận đã thu;
 * CMS theo dõi trạng thái và báo cho người gửi.
 */
export interface PayRequest {
  id: string;
  cardId: string;
  customerName: string;
  customerPhone: string;
  packageName: string;
  /** Đợt đang thu, VD "Đợt 2/3" */
  installmentLabel: string;
  amount: number;
  /** POS chi nhánh nhận yêu cầu */
  branchId: string;
  note: string;
  createdAt: string;
  createdBy: string;
  status: PayRequestStatus;
  /** Kết quả từ POS */
  resolvedAt?: string;
  resolvedBy?: string;
  paymentId?: string;
  amountPaid?: number;
  method?: PaymentMethod;
  txnRef?: string;
  change?: number;
  tendered?: number;
  reason?: string;
}

export interface TreatmentConfig {
  /** Cảnh báo sắp hết khi còn ≤ N buổi */
  lowSessions: number;
  /** Thẻ giá trị: cảnh báo khi còn ≤ N% mệnh giá */
  lowValuePct: number;
  /** Cảnh báo sắp hết hạn trước N ngày */
  expiringDays: number;
  /** Nhắc đợt thanh toán sắp đến hạn trước N ngày */
  dueSoonDays: number;
  /** Có đợt quá hạn thì chặn trừ buổi (cần quản lý duyệt) */
  blockOverdue: boolean;
  /** Không cho dùng vượt số tiền đã đóng (cần quản lý duyệt) */
  limitByPaid: boolean;
  /** Phí hoàn tiền mặc định (% trên số tiền được hoàn) */
  refundFeePct: number;
  /** Phiếu hoàn từ mức này cần quản lý duyệt */
  refundApprovalThreshold: number;
  /** Khoảng cách mặc định giữa các đợt thanh toán (ngày) */
  defaultIntervalDays: number;
  /** Được hủy lượt trừ buổi trong N giờ */
  cancelUsageHours: number;
}

export const DEFAULT_CONFIG: TreatmentConfig = {
  lowSessions: 2,
  lowValuePct: 20,
  expiringDays: 30,
  dueSoonDays: 7,
  blockOverdue: true,
  limitByPaid: true,
  refundFeePct: 10,
  refundApprovalThreshold: 2_000_000,
  defaultIntervalDays: 30,
  cancelUsageHours: 24,
};

export const DEFAULT_PRICING: PackagePricing = {
  branchPrices: {},
  promo: null,
  maxDiscountPct: 100,
  installmentSurchargePct: 0,
  intervalDays: 30,
  allowRefund: true,
  refundFeePct: 10,
  extensionFeePerMonth: 0,
  maxExtensionMonths: 6,
};

/** Chính sách giá đầy đủ của gói (dữ liệu cũ chưa có trường nào thì lấy mặc định) */
export const pricingOf = (p: Pick<TreatmentPackage, 'pricing'> | undefined): PackagePricing => ({ ...DEFAULT_PRICING, ...(p?.pricing || {}) });

export const policyOf = (p: Pick<TreatmentPackage, 'pricing'> | undefined): CardPolicy => {
  const x = pricingOf(p);
  return { allowRefund: x.allowRefund, refundFeePct: x.refundFeePct, extensionFeePerMonth: x.extensionFeePerMonth, maxExtensionMonths: x.maxExtensionMonths };
};

// ---------------------------------------------------------------------------

export const KIND_LABEL: Record<CardKind, string> = { session: 'Thẻ buổi', value: 'Thẻ giá trị' };
export const VALIDITY_LABEL: Record<ValidityBasis, string> = { purchase: 'Từ ngày mua', first_use: 'Từ buổi dùng đầu', unlimited: 'Không thời hạn' };
/** Thẻ tính hạn từ buổi đầu: mặc định phải kích hoạt trong 3 tháng */
export const DEFAULT_ACTIVATION_MONTHS = 3;
export const ROLE_LABEL: Record<ItemRole, string> = { main: 'Chính', bonus: 'Tặng kèm' };
export const SOURCE_LABEL: Record<CardSource, string> = { pos: 'Bán qua POS', import: 'Chuyển từ MySpa' };
export const PACKAGE_CATEGORIES = ['Chăm sóc da', 'Nâng cơ – trẻ hóa', 'Triệt lông', 'Body', 'Thẻ trả trước'];

/** Dịch vụ dùng được trong thẻ: dịch vụ bán tại POS + dịch vụ chỉ làm theo liệu trình */
export const TREATMENT_SERVICES: TService[] = [
  ...CATALOG.filter(c => c.type === 'service').map(c => ({ id: c.id, code: c.code, name: c.name, group: c.group, price: c.price })),
  { id: 't1', code: 'DV-TRIETLONG', name: 'Triệt lông nách', group: 'Triệt lông', price: 500_000 },
  { id: 't2', code: 'DV-DIENDI', name: 'Điện di vitamin C', group: 'Chăm sóc da', price: 400_000 },
  { id: 't3', code: 'DV-BODYDA', name: 'Massage body đá nóng', group: 'Body', price: 700_000 },
  { id: 't4', code: 'DV-NHANMUN', name: 'Lấy nhân mụn', group: 'Chăm sóc da', price: 300_000 },
  { id: 't5', code: 'DV-PEEL', name: 'Peel da sinh học', group: 'Chăm sóc da', price: 900_000 },
];
export const serviceOf = (id: string) => TREATMENT_SERVICES.find(s => s.id === id);

const iso = (d: Date) => d.toISOString();

export const seedPackages = (now: Date): TreatmentPackage[] => {
  const at = iso(now);
  const ymdOf = (d: Date) => iso(d).slice(0, 10);
  const shift = (days: number) => ymdOf(new Date(now.getTime() + days * 86400000));
  const p = (x: Omit<TreatmentPackage, 'updatedAt' | 'note' | 'active' | 'faceValue'> & Partial<Pick<TreatmentPackage, 'note' | 'active' | 'faceValue'>>): TreatmentPackage =>
    ({ note: '', active: true, faceValue: 0, updatedAt: at, ...x });
  // k1, k2 trùng mã hàng gói trong danh mục POS để đơn POS cũ vẫn khớp gói
  return [
    p({ id: 'k1', code: 'GOI-HIFU5', name: 'Gói Hifu 5 buổi', category: 'Nâng cơ – trẻ hóa', kind: 'session', items: [{ serviceId: 's2', sessions: 5, role: 'main' }], price: 60_000_000, validityMonths: 12, maxInstallments: 3, minFirstPct: 30,
      pricing: { branchPrices: { q1: 62_000_000 }, maxDiscountPct: 10, installmentSurchargePct: 3, intervalDays: 30, refundFeePct: 15, extensionFeePerMonth: 500_000, maxExtensionMonths: 3 } }),
    p({ id: 'k2', code: 'GOI-TRIMUN10', name: 'Gói trị mụn 10 buổi', category: 'Chăm sóc da', kind: 'session', items: [{ serviceId: 's6', sessions: 10, role: 'main' }, { serviceId: 't4', sessions: 2, role: 'bonus' }], price: 10_000_000, validityMonths: 8, maxInstallments: 2, minFirstPct: 50,
      pricing: { maxDiscountPct: 15, intervalDays: 30, refundFeePct: 10, extensionFeePerMonth: 200_000, maxExtensionMonths: 3 } }),
    p({ id: 'k3', code: 'GOI-CSD10', name: 'Combo chăm sóc da 10 + 5', category: 'Chăm sóc da', kind: 'session', items: [{ serviceId: 's5', sessions: 10, role: 'main' }, { serviceId: 't2', sessions: 5, role: 'bonus' }], price: 6_000_000, validityMonths: 6, maxInstallments: 2, minFirstPct: 50,
      pricing: { promo: { price: 4_990_000, from: shift(-10), to: shift(20), label: 'Ưu đãi tháng 10' }, maxDiscountPct: 0, intervalDays: 30, refundFeePct: 10, extensionFeePerMonth: 150_000, maxExtensionMonths: 2 } }),
    p({ id: 'k4', code: 'GOI-TRIETLONG12', name: 'Triệt lông nách 12 buổi', category: 'Triệt lông', kind: 'session', items: [{ serviceId: 't1', sessions: 12, role: 'main' }], price: 4_800_000, validityMonths: 18, maxInstallments: 3, minFirstPct: 30,
      pricing: { branchPrices: { q10: 4_500_000 }, maxDiscountPct: 10, intervalDays: 30, refundFeePct: 10, maxExtensionMonths: 6 } }),
    p({ id: 'k5', code: 'GOI-BODY10', name: 'Body đá nóng 10 buổi', category: 'Body', kind: 'session', items: [{ serviceId: 't3', sessions: 10, role: 'main' }], price: 6_000_000, validityMonths: 6, maxInstallments: 2, minFirstPct: 50,
      pricing: { maxDiscountPct: 10, intervalDays: 21, refundFeePct: 10, extensionFeePerMonth: 200_000, maxExtensionMonths: 2 } }),
    p({ id: 'k6', code: 'GOI-VIP', name: 'Gói trẻ hóa VIP', category: 'Nâng cơ – trẻ hóa', kind: 'session', items: [{ serviceId: 's1', sessions: 1, role: 'main' }, { serviceId: 's2', sessions: 2, role: 'main' }, { serviceId: 's5', sessions: 6, role: 'bonus' }], price: 90_000_000, validityMonths: 12, maxInstallments: 4, minFirstPct: 25, note: 'Ưu tiên khách hạng Vàng trở lên',
      pricing: { maxDiscountPct: 5, installmentSurchargePct: 5, intervalDays: 30, refundFeePct: 20, extensionFeePerMonth: 1_000_000, maxExtensionMonths: 3 } }),
    p({ id: 'k7', code: 'THE-TT20', name: 'Thẻ trả trước 20 triệu', category: 'Thẻ trả trước', kind: 'value', items: [], faceValue: 24_000_000, price: 20_000_000, validityMonths: 12, maxInstallments: 1, minFirstPct: 100, note: 'Mệnh giá 24 triệu, dùng cho mọi dịch vụ',
      pricing: { maxDiscountPct: 0, allowRefund: false, refundFeePct: 0, maxExtensionMonths: 0 } }),
    p({ id: 'k8', code: 'GOI-PEEL6', name: 'Peel da sinh học 6 buổi', category: 'Chăm sóc da', kind: 'session', items: [{ serviceId: 't5', sessions: 6, role: 'main' }], price: 4_500_000, validityMonths: 6, maxInstallments: 2, minFirstPct: 50, active: false, note: 'Tạm ngưng bán – chờ giá mới' }),
  ];
};
